import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Moon,
  MapPin,
  X,
  Send,
  Loader2,
  Clock,
  Users,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Card } from './AdminPanelPrimitives';
import { AdminAutocomplete } from './AdminAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_OPTIONS = [
  { id: 'in_app', label: 'In-app' },
  { id: 'push',   label: 'Push' },
  { id: 'email',  label: 'Email' },
];

const formatScheduledFor = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const cityRow = (row) => (
  <>
    <MapPin size={14} color="#5B7EFF" />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{row.city}</span>
      <span style={{ fontSize: 11, color: '#777' }}>
        {row.country || '—'} · {row.venue_count} venue{row.venue_count === 1 ? '' : 's'}
      </span>
    </div>
  </>
);

export const PromoteTonightCard = ({ headers }) => {
  const [pickedCity, setPickedCity] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState({ in_app: true, push: true, email: false });
  const [scheduleUtc, setScheduleUtc] = useState(''); // empty = send now
  const [sending, setSending] = useState(false);

  const [scheduled, setScheduled] = useState([]);

  const fetchScheduled = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promote-tonight/scheduled`, { headers });
      setScheduled(res.data || []);
    } catch (err) {
      // Silent — list is a nice-to-have. Surface only on outright auth failure.
      if (err.response?.status === 403) {
        toast.error('Admin only');
      }
    }
  }, [headers]);

  useEffect(() => { fetchScheduled(); }, [fetchScheduled]);

  const fetchPreview = useCallback(async (city) => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/admin/promote-tonight/preview`, { city }, { headers });
      setPreview(res.data);
      setTitle(res.data?.defaults?.title || `Tonight in ${city}`);
      setMessage(res.data?.defaults?.message || '');
      setScheduleUtc('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tonight preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [headers]);

  const pickCity = (row) => {
    setPickedCity(row);
    fetchPreview(row.city);
  };
  const clearCity = () => {
    setPickedCity(null);
    setPreview(null);
    setTitle('');
    setMessage('');
    setScheduleUtc('');
  };

  const selectedChannelList = useMemo(
    () => Object.keys(channels).filter((k) => channels[k]),
    [channels],
  );

  const send = async () => {
    if (!pickedCity || !preview) {
      toast.error('Pick a city first.');
      return;
    }
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required.');
      return;
    }
    if (selectedChannelList.length === 0) {
      toast.error('Pick at least one delivery channel.');
      return;
    }
    const confirmMessage = scheduleUtc
      ? `Schedule for ${formatScheduledFor(scheduleUtc)} · send to ${preview.recipient_count} recipient${preview.recipient_count === 1 ? '' : 's'} via ${selectedChannelList.join(' + ')}?`
      : `Send NOW to ${preview.recipient_count} recipient${preview.recipient_count === 1 ? '' : 's'} via ${selectedChannelList.join(' + ')}?`;
    if (!window.confirm(confirmMessage)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/promote-tonight/send`, {
        city: pickedCity.city,
        title: title.trim(),
        message: message.trim(),
        channels: selectedChannelList,
        schedule_at: scheduleUtc || null,
      }, { headers });
      if (res.data?.status === 'scheduled') {
        toast.success(`Scheduled for ${formatScheduledFor(res.data.scheduled_for)} · ${res.data.expected_recipient_count} recipients`);
      } else {
        toast.success(`Sent · ${res.data?.recipient_count || 0} recipients`);
      }
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote tonight');
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (broadcastId) => {
    if (!window.confirm('Cancel this scheduled broadcast?')) return;
    try {
      await axios.delete(`${API}/admin/promote-tonight/scheduled/${broadcastId}`, { headers });
      toast.success('Cancelled');
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <Card data-testid="promote-tonight-card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#FF6B6B,#FFB454)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#FFF' }}>
          <Moon size={16} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 20 }}>Promote tonight in a city</h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>
            Push every guest, venue owner and host tied to one city — timed for their local evening.
          </p>
        </div>
      </div>

      {/* City picker */}
      <div style={{ marginTop: 14 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>City</p>
        {pickedCity ? (
          <div data-testid="promote-tonight-city-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 999, background: '#EEF2FF', border: '1px solid #C7D0FF' }}>
            <MapPin size={12} color="#5B7EFF" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2945' }}>{pickedCity.city}</span>
            <span style={{ fontSize: 11, color: '#5B7EFF' }}>{pickedCity.country || ''}</span>
            <button onClick={clearCity} data-testid="promote-tonight-city-clear" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={12} /></button>
          </div>
        ) : (
          <AdminAutocomplete
            endpoint="/admin/directory/cities"
            headers={headers}
            placeholder="Search a city to promote tonight…"
            minChars={0}
            renderRow={cityRow}
            onSelect={pickCity}
            emptyHint="No matching cities — only cities with venues/listings appear here."
            dataTestId="promote-tonight-city-autocomplete"
          />
        )}
      </div>

      {previewLoading ? (
        <div style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8, color: '#666' }}>
          <Loader2 size={14} className="animate-spin" /> Computing tonight in {pickedCity?.city}…
        </div>
      ) : null}

      {preview ? (
        <div data-testid="promote-tonight-preview" style={{ marginTop: 14, padding: 14, borderRadius: 14, background: 'linear-gradient(135deg,#0F1115,#1A1D24)', color: '#FFF' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Local now</p>
              <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700 }}>{preview.tonight_window.local_now_label}</p>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{preview.timezone}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tonight window</p>
              <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700 }}>{preview.tonight_window.window_label}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Open right now</p>
              <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700, color: '#00D976' }}>{preview.open_venues_now}</p>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>venue{preview.open_venues_now === 1 ? '' : 's'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Recipients</p>
              <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700, color: '#5BC0EB' }}>{preview.recipient_count}</p>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{preview.breakdown?.venue_owners || 0} owners · {preview.breakdown?.active_guests || 0} guests</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Schedule */}
      {preview ? (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Send time</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              data-testid="promote-tonight-slot-now"
              onClick={() => setScheduleUtc('')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                border: `1px solid ${scheduleUtc === '' ? '#111' : '#E7E7E7'}`,
                background: scheduleUtc === '' ? '#111' : '#FFF',
                color: scheduleUtc === '' ? '#FFF' : '#444',
              }}
            >
              <Sparkles size={12} /> Send now
            </button>
            {(preview.tonight_window.suggested_slots || []).map((slot) => (
              <button
                key={slot.utc_iso}
                data-testid={`promote-tonight-slot-${slot.utc_iso}`}
                onClick={() => setScheduleUtc(slot.utc_iso)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  border: `1px solid ${scheduleUtc === slot.utc_iso ? '#111' : '#E7E7E7'}`,
                  background: scheduleUtc === slot.utc_iso ? '#111' : '#FFF',
                  color: scheduleUtc === slot.utc_iso ? '#FFF' : '#444',
                }}
              >
                <Clock size={12} /> {slot.label}
              </button>
            ))}
          </div>
          {(preview.tonight_window.suggested_slots || []).length === 0 ? (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#888' }}>
              Suggested slots already passed in {preview.city}. Use &quot;Send now&quot; to push immediately.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Channels */}
      {preview ? (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Delivery channels</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {CHANNEL_OPTIONS.map((c) => {
              const active = channels[c.id];
              return (
                <button
                  key={c.id}
                  data-testid={`promote-tonight-channel-${c.id}`}
                  onClick={() => setChannels((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                  style={{
                    padding: '8px 14px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                    border: `1px solid ${active ? '#111' : '#E7E7E7'}`,
                    background: active ? '#111' : '#FFF',
                    color: active ? '#FFF' : '#444',
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Title + Message */}
      {preview ? (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="promote-tonight-title"
              style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 14 }}
            />
          </label>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="promote-tonight-message"
              rows={3}
              style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 13.5, resize: 'vertical' }}
            />
          </label>
        </div>
      ) : null}

      {/* Send */}
      {preview ? (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={send}
            disabled={sending || preview.recipient_count === 0}
            data-testid="promote-tonight-send"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 12, border: 'none', cursor: sending ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13,
              background: preview.recipient_count === 0 ? '#9aa0a6' : '#DD0608', color: '#FFF',
            }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {scheduleUtc ? `Schedule for ${formatScheduledFor(scheduleUtc)}` : 'Send now'}
          </button>
          <span data-testid="promote-tonight-recipient-count" style={{ fontSize: 12, color: '#666' }}>
            <Users size={12} style={{ verticalAlign: -1, marginRight: 4 }} /> {preview.recipient_count} recipient{preview.recipient_count === 1 ? '' : 's'}
          </span>
        </div>
      ) : null}

      {/* Scheduled list */}
      {scheduled.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Scheduled</p>
          <div data-testid="promote-tonight-scheduled-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {scheduled.map((row) => (
              <div key={row.id} data-testid={`promote-tonight-scheduled-${row.id}`} style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid #E7E7E7', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Clock size={14} color="#5B7EFF" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777' }}>
                    {row.audience?.city || '—'} · {formatScheduledFor(row.scheduled_for)} · {row.status}
                    {row.expected_recipient_count ? ` · ${row.expected_recipient_count} expected` : ''}
                  </p>
                </div>
                {row.status === 'pending' ? (
                  <button
                    data-testid={`promote-tonight-cancel-${row.id}`}
                    onClick={() => cancelScheduled(row.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(221,6,8,0.08)', border: '1px solid #DD060833', borderRadius: 9999, padding: '5px 10px', cursor: 'pointer', color: '#DD0608', fontWeight: 700, fontSize: 11 }}
                  >
                    <Trash2 size={11} /> Cancel
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export default PromoteTonightCard;
